/**
 * Normalize location name by removing common prefixes and standardizing format
 * @param {string} name - Location name to normalize
 * @returns {string} Normalized name
 */
const normalizeLocationName = (name) => {
  if (!name) return ''

  let normalized = name.toLowerCase().trim()

  // Remove common prefixes
  const prefixes = [
    'daerah khusus ibukota',
    'kecamatan',
    'kelurahan',
    'kabupaten',
    'kota',
    'provinsi',
    'kec.',
    'kel.',
    'kab.',
  ]

  for (const prefix of prefixes) {
    if (normalized.startsWith(prefix + ' ')) {
      normalized = normalized.substring(prefix.length).trim()
    }
  }

  return normalized
}

/**
 * Match location name with fuzzy matching
 * @param {string} apiName - Name from API response
 * @param {string} listName - Name from dropdown list
 * @returns {boolean} True if names match
 */
export const matchLocationName = (apiName, listName) => {
  if (!apiName || !listName) return false

  const normalizedApi = normalizeLocationName(apiName)
  const normalizedList = normalizeLocationName(listName)

  // Handle DKI Jakarta special case - need to match specific areas
  if (normalizedApi.includes('jakarta') && normalizedList.includes('jakarta')) {
    // For Jakarta areas, check the specific direction (barat, selatan, timur, etc.)
    const jakartaAreas = ['barat', 'selatan', 'timur', 'utara', 'pusat']

    for (const area of jakartaAreas) {
      const apiHasArea = normalizedApi.includes(area)
      const listHasArea = normalizedList.includes(area)

      // If both mention jakarta AND an area, they must have the SAME area
      if (apiHasArea && listHasArea) {
        return (
          apiHasArea &&
          listHasArea &&
          normalizedApi.includes(area) &&
          normalizedList.includes(area)
        )
      }

      // If only one has the area, it's not a match
      if (apiHasArea !== listHasArea) {
        return false
      }
    }

    // Both have jakarta but no specific area mentioned - they match
    return true
  }

  // Exact match after normalization
  if (normalizedApi === normalizedList) {
    return true
  }

  // Split into words for better matching
  const apiWords = normalizedApi.split(/\s+/).filter((w) => w.length > 2)
  const listWords = normalizedList.split(/\s+/).filter((w) => w.length > 2)

  if (apiWords.length === 0 || listWords.length === 0) {
    // Fall back to simple contains
    return (
      normalizedApi.includes(normalizedList) ||
      normalizedList.includes(normalizedApi)
    )
  }

  // All significant words from the shorter name should be in the longer name
  const shorterWords =
    apiWords.length <= listWords.length ? apiWords : listWords
  const longerWords = apiWords.length > listWords.length ? apiWords : listWords

  const matchedWords = shorterWords.filter((word) =>
    longerWords.some((w) => w.includes(word) || word.includes(w)),
  )

  // Require at least 70% of words to match
  return matchedWords.length >= shorterWords.length * 0.7
}

/**
 * Geocode an address to get lat/long coordinates
 * @param {string} address - Full address string (e.g., "Bayah Timur, Bayah, Kabupaten Lebak, Banten")
 * @returns {Promise<Object>} Object with lat, lng coordinates
 */
export const geocodeAddress = async (address) => {
  const API_ENDPOINT =
    'https://script.google.com/macros/s/AKfycbxKn7ZRhuQPz6Go8tsIUu-jqIIO02d6ZjT8EwHItIUlQgv-U50chdzrhc0Z0oDp0MTTyQ/exec'
  const SECRET_KEY = 'LuM84zPEbZRfjjWbH2cdvq3NGuSK2wvm'

  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
      body: JSON.stringify({
        secret: SECRET_KEY,
        address: address,
        action: 'geo',
      }),
    })

    if (!response.ok) {
      throw new Error(
        `Geocoding API request failed with status ${response.status}`,
      )
    }

    const data = await response.json()

    if (data.location && data.location.lat && data.location.lng) {
      return {
        lat: data.location.lat,
        lng: data.location.lng,
        fullResponse: data,
      }
    }

    throw new Error('Invalid geocoding response format')
  } catch (error) {
    console.error('Error geocoding address:', error)
    throw error
  }
}

/**
 * Fetch nearby cities based on latitude and longitude coordinates
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Array>} Array of city objects
 */
export const fetchNearbyCities = async (lat, lng) => {
  const API_ENDPOINT =
    'https://script.google.com/macros/s/AKfycbxKn7ZRhuQPz6Go8tsIUu-jqIIO02d6ZjT8EwHItIUlQgv-U50chdzrhc0Z0oDp0MTTyQ/exec'
  const SECRET_KEY = 'LuM84zPEbZRfjjWbH2cdvq3NGuSK2wvm'

  try {
    // Using POST with text/plain to avoid CORS preflight
    // Send JSON as plain text string
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
      body: JSON.stringify({
        secret: SECRET_KEY,
        action: 'reverse',
        lat,
        lng,
      }),
    })

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`)
    }

    const data = await response.json()

    // Handle the new Google Apps Script API response
    if (data.success && data.administrative) {
      const admin = data.administrative

      // Build fullData object with all administrative levels
      const fullData = {
        province: admin.province?.long || '',
        city: admin.city?.long || '',
        district: admin.district?.long || '',
        subdistrict: admin.subdistrict?.long || '',
        postalCode: admin.postal_code?.long || '',
        formatted_address: data.full_address || '',
        location: data.location || null,
      }

      // Build display label
      const parts = []
      if (fullData.district) parts.push(fullData.district)
      if (fullData.city) parts.push(fullData.city)
      if (fullData.province) parts.push(fullData.province)

      const label =
        parts.length > 0 ? parts.join(', ') : fullData.formatted_address
      const value =
        fullData.city || fullData.district || fullData.formatted_address

      return [
        {
          label,
          value,
          fullData,
        },
      ]
    }

    // Fallback: Handle Google Places-style reverse geocoding response (old format)
    if (data.address_components && Array.isArray(data.address_components)) {
      // Extract location components from address_components
      const components = {}

      data.address_components.forEach((component) => {
        const types = component.types || []
        const longName = component.long_name
        const shortName = component.short_name

        // Map different administrative levels
        if (
          types.includes('administrative_area_level_4') ||
          types.includes('subdistrict')
        ) {
          components.subdistrict = longName
        }
        if (
          types.includes('administrative_area_level_3') ||
          types.includes('district')
        ) {
          components.district = longName
        }
        if (
          types.includes('administrative_area_level_2') ||
          types.includes('city') ||
          types.includes('administrative_area_level_1')
        ) {
          if (!components.city && longName.includes('Kota')) {
            components.city = longName
          }
        }
        if (
          types.includes('administrative_area_level_1') ||
          types.includes('province')
        ) {
          components.province = longName
        }
      })

      // Build display label
      const parts = []
      if (components.district) parts.push(components.district)
      if (components.city) parts.push(components.city)
      if (components.province) parts.push(components.province)

      const label = parts.length > 0 ? parts.join(', ') : data.formatted_address
      const value =
        components.city || components.district || data.formatted_address

      return [
        {
          label,
          value,
          fullData: {
            ...components,
            formatted_address: data.formatted_address,
            place_id: data.place_id,
            location: data.location,
          },
        },
      ]
    }

    // Fallback: handle array of locations
    let locations = []
    if (Array.isArray(data)) {
      locations = data
    } else if (data.cities && Array.isArray(data.cities)) {
      locations = data.cities
    } else if (data.data && Array.isArray(data.data)) {
      locations = data.data
    }

    // Map to react-select format with detailed labels
    return locations.map((item) => {
      const parts = []

      if (item.district || item.kecamatan) {
        parts.push(item.district || item.kecamatan)
      }

      if (item.city || item.kabupaten || item.kota || item.name) {
        parts.push(item.city || item.kabupaten || item.kota || item.name)
      }

      if (item.province || item.provinsi) {
        parts.push(item.province || item.provinsi)
      }

      const label =
        parts.length > 0 ? parts.join(', ') : item.label || String(item)
      const value = item.city || item.kota || item.name || item.label || label

      return {
        label,
        value,
        fullData: item,
      }
    })
  } catch (error) {
    console.error('Error fetching cities:', error)
    throw error
  }
}
